/* ==========================================================================
   Vision Attire — garment template

   Front and back flats of the signature cut, split into the nine zones the
   builder colours independently:

     body · side · waistband · piping · bias · logoLeft · logoRight
     · logoBand · laces

   Rebuilt 16 Aug 2026 from four zones to nine, against the Vazo/Kickflip
   reference. The old four-zone colourways in catalogue.json still work —
   `piping` and `bias` fall back to `trim`, `laces` falls back to
   `drawstring` — so product cards and the accent demo needed no change.

   One renderer drives the builder preview and every product card, so a
   colourway stays data rather than photography. Length changes the real hem.

   ponytail: hand-authored SVG, not photo-derived. The production version
   wants a straight-on shot of a real pair separated into these same layers;
   the zone names already match, so it drops in. The logo marks are a neutral
   placeholder chevron — Vision Attire's real logo vector replaces `mark()`
   and nothing else moves.
   ========================================================================== */

const VA_GARMENT = (() => {
  let uid = 0;

  // Hem drops as the length goes up. Crotch stays put, which is how the
  // real garment behaves — a 17" has a longer inseam, not a lower rise.
  const hemY = (len) => 320 + (Math.max(13, Math.min(17, Number(len) || 14)) - 13) * 27;

  // Fitted mid-thigh cut with the curved leg opening off the reference shot:
  // hem sits lowest at the outer seam and sweeps up toward the inside.
  const bodyPath = (H) => `
    M68,60 L332,60
    C346,60 354,84 354,122
    L349,${H - 46}
    C348,${H - 18} 341,${H - 6} 326,${H - 6}
    L246,${H - 10}
    C224,${H - 12} 211,${H - 40} 207,${H - 78}
    L203,282
    L197,282
    L193,${H - 78}
    C189,${H - 40} 176,${H - 12} 154,${H - 10}
    L74,${H - 6}
    C59,${H - 6} 52,${H - 18} 51,${H - 46}
    L46,122
    C46,84 54,60 68,60 Z`.replace(/\s+/g, ' ');

  // Outer edge of each leg, used for the hem binding (the bias).
  const hemLeft  = (H) => `M51,${H - 46} C52,${H - 18} 59,${H - 6} 74,${H - 6} L154,${H - 10} C176,${H - 12} 189,${H - 40} 193,${H - 78}`;
  const hemRight = (H) => `M349,${H - 46} C348,${H - 18} 341,${H - 6} 326,${H - 6} L246,${H - 10} C224,${H - 12} 211,${H - 40} 207,${H - 78}`;

  // Side panels: narrow tapered bands down each outer edge, clipped to the body.
  const panelL = (H) => `M26,48 L78,48 L64,${H + 26} L6,${H + 26} Z`;
  const panelR = (H) => `M374,48 L322,48 L336,${H + 26} L394,${H + 26} Z`;
  const pipeL  = (H) => `M78,48 L64,${H + 26}`;
  const pipeR  = (H) => `M322,48 L336,${H + 26}`;

  // Double V: a chevron band spanning the body, pointing down at centre.
  const chevron = (yTop, depth, th) =>
    `M40,${yTop} L200,${yTop + depth} L360,${yTop} L360,${yTop + th} L200,${yTop + depth + th} L40,${yTop + th} Z`;

  // Curved front seam that gives the cut its layered look.
  const seamL = (H) => `M96,64 C104,140 116,206 150,${H - 30}`;
  const seamR = (H) => `M304,64 C296,140 284,206 250,${H - 30}`;

  const esc = (s) => String(s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

  /* Placeholder maker's mark. Swap this one function for the real logo path
     when the vector arrives — every call site already passes a fill. */
  const mark = (cx, cy, s, fill, op = 1) => {
    const w = s, h = s * 0.86, t = s * 0.42;
    return `<path d="M${cx - w},${cy - h} L${cx},${cy + h} L${cx + w},${cy - h} L${cx + w - t},${cy - h} L${cx},${cy + h - t * 1.5} L${cx - w + t},${cy - h} Z" fill="${fill}" opacity="${op}"/>`;
  };

  /**
   * @param {object} o
   * @param {string} o.body        main body colour
   * @param {string} o.side        side panel colour
   * @param {string} o.waistband   waistband colour
   * @param {string} o.piping      vertical piping (falls back to o.trim)
   * @param {string} o.bias        hem / leg binding (falls back to o.trim)
   * @param {string} o.logoLeft    left leg mark
   * @param {string} o.logoRight   right leg mark
   * @param {string} o.logoBand    waistband mark
   * @param {string} o.laces       drawstring (falls back to o.drawstring)
   * @param {string} o.trim        legacy: sets piping + bias together
   * @param {string} o.drawstring  legacy: 'black' | 'white' | hex
   * @param {number} o.length      13–17 (inches, halves allowed)
   * @param {string} o.treatment   'plain' | 'ombre' | 'pattern' | 'image'
   * @param {string[]} o.gradient  [from, to] for ombre
   * @param {boolean} o.stripes    side stripes on/off
   * @param {'front'|'back'} o.view
   * @param {string} o.label       accessible description
   */
  function render(o = {}) {
    const id = 'g' + (++uid);
    const L = Number(o.length) || 14;
    const H = hemY(L);
    const back = o.view === 'back';

    const named = (v) => v === 'white' ? '#F4F4F2' : v === 'black' ? '#101014' : v;

    const body = o.body || '#101014';
    const side = o.side || '#F4F4F2';
    const band = o.waistband || '#101014';
    const trim = o.trim || '#F4F4F2';
    const pipe = o.piping || trim;
    const bias = o.bias || trim;
    const lace = named(o.laces || o.drawstring || '#101014');
    const lgL  = o.logoLeft  || bias;
    const lgR  = o.logoRight || bias;
    const lgB  = o.logoBand  || pipe;

    const treat = o.treatment || 'plain';
    const grad = o.gradient || null;
    const style = o.style || 'stripe';

    // Body fill: solid, or a graded fade when the style is ombre. The fade's
    // second colour is the `side` zone — one zone list serves all three styles.
    let bodyFill = body;
    let defsExtra = '';
    if (style === 'ombre') {
      bodyFill = `url(#${id}-omb)`;
      const to = (grad && grad[1]) || side;
      defsExtra += `<linearGradient id="${id}-omb" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stop-color="${body}"/><stop offset="1" stop-color="${to}"/>
      </linearGradient>`;
    }
    if (treat === 'pattern') {
      bodyFill = `url(#${id}-pat)`;
      defsExtra += `<pattern id="${id}-pat" width="26" height="26" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <rect width="26" height="26" fill="${body}"/>
        <rect width="13" height="26" fill="${pipe}" opacity=".5"/>
      </pattern>`;
    } else if (treat === 'image') {
      bodyFill = `url(#${id}-img)`;
      defsExtra += `<linearGradient id="${id}-img" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stop-color="${body}"/><stop offset=".5" stop-color="${pipe}" stop-opacity=".45"/><stop offset="1" stop-color="${body}"/>
      </linearGradient>`;
    }

    // The three styles the client named on 16 Aug. Ombre needs no panel art —
    // the gradient above is the whole style. The other two reuse `side` as
    // their feature colour, which is why the zone carries a per-style label.
    const V1 = chevron(122, 78, 32), V2 = chevron(198, 78, 32);
    const styleArt =
      style === 'ombre' ? '' :
      style === 'doubleV' ? `
  <g clip-path="url(#${id}-clip)">
    <path d="${V1}" fill="${side}"/>
    <path d="${V2}" fill="${side}"/>
  </g>
  <g clip-path="url(#${id}-clip)" fill="none" stroke="${pipe}" stroke-width="3">
    <path d="${V1}"/>
    <path d="${V2}"/>
  </g>` : `
  <g clip-path="url(#${id}-clip)">
    <path d="${panelL(H)}" fill="${side}"/>
    <path d="${panelR(H)}" fill="${side}"/>
  </g>
  <g clip-path="url(#${id}-clip)" fill="none" stroke="${pipe}" stroke-width="4.5">
    <path d="${pipeL(H)}"/>
    <path d="${pipeR(H)}"/>
  </g>`;

    const stripes = o.stripes ? `
      <g clip-path="url(#${id}-clip)" opacity=".92">
        <path d="M50,48 L36,${H + 26}" stroke="${pipe}" stroke-width="6" fill="none"/>
        <path d="M62,48 L48,${H + 26}" stroke="${pipe}" stroke-width="3.4" fill="none"/>
        <path d="M350,48 L364,${H + 26}" stroke="${pipe}" stroke-width="6" fill="none"/>
        <path d="M338,48 L352,${H + 26}" stroke="${pipe}" stroke-width="3.4" fill="none"/>
      </g>` : '';

    // Front carries the fly seam and drawstring; back carries a centre seam
    // and a yoke, and the leg marks sit lower.
    const seams = back
      ? `<path d="M200,62 L200,${H - 96}"/><path d="M104,92 C150,116 250,116 296,92"/>`
      : `<path d="${seamL(H)}"/><path d="${seamR(H)}"/><path d="M200,66 L200,278"/>`;

    const drawstring = back ? '' : `
  <g fill="none" stroke="${lace}" stroke-width="4.6" stroke-linecap="round">
    <path d="M186,52 C177,68 174,88 178,110"/>
    <path d="M214,52 C223,68 226,88 222,110"/>
    <path d="M186,50 C193,38 207,38 214,50"/>
  </g>
  <circle cx="178" cy="113" r="3.4" fill="${lace}"/>
  <circle cx="222" cy="113" r="3.4" fill="${lace}"/>`;

    const legY = back ? H - 104 : H - 132;
    const legMarks = `
  <g clip-path="url(#${id}-clip)">
    ${mark(112, legY, 15, lgL)}
    ${mark(288, legY, 15, lgR)}
  </g>`;

    const bandMark = mark(back ? 200 : 288, 39, 11, lgB, .95);

    const label = o.label || `${back ? 'Back' : 'Front'} view of the shorts`;

    return `<svg viewBox="0 0 400 ${H + 24}" role="img" aria-label="${esc(label)}" xmlns="http://www.w3.org/2000/svg">
  <defs>
    <clipPath id="${id}-clip"><path d="${bodyPath(H)}"/></clipPath>
    ${defsExtra}
    <linearGradient id="${id}-sh" x1="0" y1="0" x2="1" y2="0">
      <stop offset="0" stop-color="#000" stop-opacity=".34"/>
      <stop offset=".26" stop-color="#000" stop-opacity="0"/>
      <stop offset=".52" stop-color="#fff" stop-opacity=".12"/>
      <stop offset=".78" stop-color="#000" stop-opacity="0"/>
      <stop offset="1" stop-color="#000" stop-opacity=".34"/>
    </linearGradient>
    <linearGradient id="${id}-sh2" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0" stop-color="#fff" stop-opacity=".09"/>
      <stop offset=".7" stop-color="#000" stop-opacity="0"/>
      <stop offset="1" stop-color="#000" stop-opacity=".24"/>
    </linearGradient>
  </defs>

  <!-- main body -->
  <path d="${bodyPath(H)}" fill="${bodyFill}"/>

  <!-- style: ombre carries no panel art, the fade above is the style -->
  ${styleArt}
  ${stripes}

  <!-- bias / hem binding -->
  <g fill="none" stroke="${bias}" stroke-width="6.5" stroke-linejoin="round" stroke-linecap="round">
    <path d="${hemLeft(H)}"/>
    <path d="${hemRight(H)}"/>
  </g>

  <!-- construction seams -->
  <g clip-path="url(#${id}-clip)" fill="none" stroke="#000" stroke-opacity=".2" stroke-width="1.6" style="pointer-events:none">
    ${seams}
  </g>
  ${legMarks}

  <!-- fabric shading, keeps it reading as cloth rather than a sticker -->
  <g clip-path="url(#${id}-clip)" style="pointer-events:none">
    <path d="${bodyPath(H)}" fill="url(#${id}-sh)"/>
    <path d="${bodyPath(H)}" fill="url(#${id}-sh2)"/>
  </g>

  <!-- waistband -->
  <rect x="46" y="16" width="308" height="46" rx="4" fill="${band}"/>
  <rect x="46" y="16" width="308" height="46" rx="4" fill="url(#${id}-sh)" style="pointer-events:none"/>
  <g stroke="${pipe}" stroke-width="1.3" opacity=".42">
    <line x1="56" y1="23" x2="344" y2="23"/>
    <line x1="56" y1="55" x2="344" y2="55"/>
  </g>
  ${back ? '' : `<g fill="${pipe}" opacity=".5">
    <circle cx="186" cy="52" r="2.6"/><circle cx="214" cy="52" r="2.6"/>
  </g>`}
  ${bandMark}
  ${drawstring}
</svg>`;
  }

  return { render, hemY, ZONE_KEYS: ['body', 'side', 'waistband', 'piping', 'bias', 'logoLeft', 'logoRight', 'logoBand', 'laces'] };
})();

if (typeof module !== 'undefined') module.exports = VA_GARMENT;
